import type { CrossTabWrite } from 'app/types/store-writes'
import { logError } from 'lib/logging'

/**
 * Opens the channel a store uses to tell the app's other open tabs about its successful writes,
 * and hands every write another tab posts to `onWrite`. Returns the function that posts one.
 * Where BroadcastChannel does not exist, posting does nothing and nothing arrives.
 */
export function openCrossTabWrites<T>(
  name: string,
  onWrite: (write: CrossTabWrite<T>) => void,
): (write: CrossTabWrite<T>) => void {
  if (typeof BroadcastChannel === `undefined`) return () => undefined
  const channel = new BroadcastChannel(`trackable:${name}`)
  channel.onmessage = (event: MessageEvent<CrossTabWrite<T>>) => onWrite(event.data)
  // Node's BroadcastChannel, which the store specs run on, otherwise holds the test process open.
  ;(channel as BroadcastChannel & { unref?: () => void }).unref?.()
  return (write) => {
    // The write already succeeded; a record the channel cannot clone must not fail the caller.
    try {
      channel.postMessage(write)
    } catch (error) {
      logError(`Could not share a ${name} write with other tabs`, error)
    }
  }
}
